import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="19338" className="hidden lg:flex gap-8 items-center">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted/50 animate-pulse rounded-lg" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-sans font-bold tracking-tight text-foreground transition-colors hover:text-primary">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuContent({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 items-center w-full mt-10">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-muted/50 animate-pulse rounded-lg" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-8 items-center text-center mt-10">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-2xl font-sans font-bold tracking-tight text-foreground hover:text-primary transition-colors" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
      <Button asChild className="mt-8 w-full max-w-xs bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg" size="lg">
        <Link to="/ReservationPage" onClick={onClose}>
          Book Now
        </Link>
      </Button>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full border-b transition-all duration-500 ease-in-out bg-background text-foreground ${isScrolled ? "bg-card backdrop-blur-md border-border/50 shadow-md py-2" : "bg-background border-transparent py-4"}`}>
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 z-50">
          <Link to="/" className="block" aria-label="Paradise Palms Home">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="16">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4 z-50">
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg" size="sm">
            <Link to="/ReservationPage">
              Book Now
            </Link>
          </Button>

          {}
          <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:bg-accent rounded-lg" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}>
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-40 bg-background/98 backdrop-blur-md pt-24 px-6 lg:hidden animate-in fade-in slide-in-from-top-5 duration-300 flex flex-col h-screen overflow-y-auto">
          <MenuProvider menu_id="16">
            <MobileMenuContent onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}